// ./src/components/features/Connectors/ConfigEditors/HubSpotConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Text,
  Anchor,
  Alert,
  Code,
  Switch,
  Group,
  Box,
  List,
  Select,
} from "@mantine/core";
import {
  IconInfoCircle,
  IconTicket,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { HubSpotConnectorConfig } from "@/types";

export interface HubSpotConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: HubSpotConnectorConfig = {
  accessToken: "",
  pipelineId: "",
  stageId: "",
  priority: "MEDIUM",
  subjectPattern: "{{formName}}: New submission",
  updateExistingContact: true,
};

// Priority options
const PRIORITY_OPTIONS = [
  { value: "LOW", label: "Low" },
  { value: "MEDIUM", label: "Medium" },
  { value: "HIGH", label: "High" },
];

/**
 * Parse and validate HubSpot connector configuration
 */
const parseHubSpotConfig = (jsonString: string): HubSpotConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const HubSpotConnectorConfigEditor: React.FC<
  HubSpotConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<HubSpotConnectorConfig>(() =>
    parseHubSpotConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseHubSpotConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: HubSpotConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle field changes
  const handleAccessTokenChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, accessToken: e.target.value });
  };

  const handlePipelineIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, pipelineId: e.target.value });
  };

  const handleStageIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, stageId: e.target.value });
  };

  const handlePriorityChange = (value: string | null) => {
    updateConfig({
      ...config,
      priority: (value as "LOW" | "MEDIUM" | "HIGH") || "MEDIUM",
    });
  };

  const handleSubjectPatternChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, subjectPattern: e.target.value });
  };

  const handleUpdateExistingContactChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, updateExistingContact: e.currentTarget.checked });
  };

  return (
    <Stack gap="md">
      {/* Setup Instructions */}
      <Alert icon={<IconTicket size={16} />} color="orange" variant="light">
        <Text size="sm" fw={500} mb="xs">
          Setup Instructions:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Go to{" "}
            <Anchor
              href="https://app.hubspot.com/private-apps/"
              target="_blank"
              rel="noopener noreferrer"
            >
              HubSpot Private Apps
            </Anchor>
          </List.Item>
          <List.Item>Click "Create a private app"</List.Item>
          <List.Item>Name it (e.g., "FormSubmits Integration")</List.Item>
          <List.Item>
            Under Scopes, enable:
            <List size="xs" withPadding mt={4}>
              <List.Item>
                <Code>crm.objects.contacts.read</Code>
              </List.Item>
              <List.Item>
                <Code>crm.objects.contacts.write</Code>
              </List.Item>
              <List.Item>
                <Code>tickets</Code>
              </List.Item>
            </List>
          </List.Item>
          <List.Item>Create app and copy the access token</List.Item>
        </List>
      </Alert>

      {/* Access Token Field */}
      <TextInput
        label="Access Token"
        placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
        description="Private App access token from HubSpot"
        value={config.accessToken}
        onChange={handleAccessTokenChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Pipeline and Stage Info Alert */}
      <Alert
        icon={<IconAlertTriangle size={16} />}
        color="yellow"
        variant="light"
      >
        <Text size="sm" fw={500} mb="xs">
          Important: Use numeric IDs, not names!
        </Text>
        <Text size="sm">
          Pipeline and Stage fields require <strong>numeric IDs</strong>, not
          names. For example, enter <Code>0</Code> not "Support Pipeline".
        </Text>
        <Text size="sm" mt="xs">
          <strong>To find IDs:</strong> In HubSpot, go to Settings → Objects →
          Tickets → Pipelines. Click on a pipeline — the ID is in the URL (e.g.,{" "}
          <Code>/pipelines/0/</Code> means ID is <Code>0</Code>). Stage IDs are
          typically <Code>1</Code>, <Code>2</Code>, <Code>3</Code>, etc.
        </Text>
      </Alert>

      {/* Pipeline ID Field */}
      <TextInput
        label="Pipeline ID (numeric)"
        placeholder="0"
        description="Leave empty to use default Support Pipeline (ID: 0)"
        value={config.pipelineId || ""}
        onChange={handlePipelineIdChange}
        disabled={disabled}
        size="md"
      />

      {/* Stage ID Field */}
      <TextInput
        label="Stage ID (numeric)"
        placeholder="1"
        description="Leave empty to use first stage automatically (typically ID: 1)"
        value={config.stageId || ""}
        onChange={handleStageIdChange}
        disabled={disabled}
        size="md"
      />

      {/* Priority Select */}
      <Select
        label="Priority"
        placeholder="Select priority"
        description="Default ticket priority"
        data={PRIORITY_OPTIONS}
        value={config.priority || "MEDIUM"}
        onChange={handlePriorityChange}
        disabled={disabled}
        size="md"
      />

      {/* Subject Pattern Field */}
      <TextInput
        label="Ticket Subject Pattern"
        placeholder="{{formName}}: New submission"
        description="Customize the ticket subject"
        value={config.subjectPattern || "{{formName}}: New submission"}
        onChange={handleSubjectPatternChange}
        disabled={disabled}
        size="md"
      />

      {/* Update Existing Contact Toggle */}
      <Box>
        <Group justify="space-between">
          <div>
            <Text size="sm">Update Existing Contacts</Text>
            <Text size="xs" c="dimmed">
              Update contact properties if contact already exists
            </Text>
          </div>
          <Switch
            checked={config.updateExistingContact !== false}
            onChange={handleUpdateExistingContactChange}
            disabled={disabled}
          />
        </Group>
      </Box>

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders for subject:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{formName}}"}</Code> — Form name
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{requestId}}"}</Code> — Submission ID
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
        </Text>
      </Alert>

      {/* How It Works */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm" fw={500} mb="xs">
          How it works:
        </Text>
        <List size="sm" spacing={4}>
          <List.Item>
            Finds contact by email (from "Reply-To" field or first email field)
            or phone
          </List.Item>
          <List.Item>
            Creates new contact or updates existing one with auto-mapped fields
            (firstName, lastName, phone, company)
          </List.Item>
          <List.Item>
            Creates a ticket with all form submission details
          </List.Item>
          <List.Item>Associates the ticket with the contact</List.Item>
        </List>
      </Alert>

      {/* Requirement Note */}
      <Alert icon={<IconInfoCircle size={16} />} color="yellow" variant="light">
        <Text size="sm">
          <strong>Note:</strong> Your form must have at least one email or phone
          field for HubSpot integration. Submissions without contact info will
          be skipped.
        </Text>
      </Alert>
    </Stack>
  );
};
